import Axios from "axios";
import { getCookie, setCookie, deleteCookie } from "cookies-next";
import { encryptData } from "./hashing.service";
import { getDeviceId } from "@/utils";

const API = Axios.create({
  baseURL: process.env.NEXT_PUBLIC_API,
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*",
    "x-device-source": "web",
  },
});

// Variables to manage token refresh flow
let isRefreshing = false;
let failedRequestsQueue = [];

// Inactivity timer
let inactivityTimer;

const startInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  inactivityTimer = setTimeout(() => {
    handleLogout();
  }, 900000);
};

// Function to handle user activity
const handleUserActivity = () => {
  startInactivityTimer();
};

// Attach event listeners for user activity
if (typeof window !== "undefined") {
  window.addEventListener("mousemove", handleUserActivity);
  window.addEventListener("keydown", handleUserActivity);
  window.addEventListener("click", handleUserActivity);
  window.addEventListener("scroll", handleUserActivity);

  // Start the timer when the page loads
  startInactivityTimer();
}

const handleLogout = () => {
  deleteCookie("access_token");
  deleteCookie("refresh_token");
  localStorage.removeItem("access_token");

  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  window.location.href = "/signin";
};

const refreshToken = async () => {
  const refreshToken = getCookie("refresh_token");
  if (!refreshToken) {
    handleLogout();
    throw new Error("No refresh token available");
  }

  try {
    const response = await API.get("/v1/auth/access-token", {
      headers: { Authorization: `Bearer ${refreshToken}` },
    });

    const { access_token, refresh_token } = response.data;

    // Store new tokens
    setCookie("access_token", access_token);
    setCookie("refresh_token", refresh_token);
    localStorage.setItem("access_token", access_token);

    return access_token;
  } catch (error) {
    handleLogout();
    throw error;
  }
};

API.interceptors.request.use((config) => {
  const token =
    getCookie("access_token") || localStorage.getItem("access_token");

  const deviceId = getDeviceId();

  if (deviceId) {
    config.headers["x-device-id"] = deviceId;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Reset the inactivity timer on every API request
  handleUserActivity();
  let signature = "";

  // Encrypt request data if it's not multipart/form-data
  if (config.method === "get" && config.params) {
    signature = encryptData(JSON.stringify(config.params));
  } else if (
    config.data &&
    !config.headers["Content-Type"]?.includes("multipart/form-data")
  ) {
    signature = encryptData(JSON.stringify(config.data));
  }
  config.headers["x-signature"] = signature;
  return config;
});

API.interceptors.response.use(
  (response) => {
    if (response.data && response.headers['x-signature']) {}
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      error.response?.data?.message?.includes("Invalid or expired token")
    ) {
      if (originalRequest._retry) {
        handleLogout();
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newAccessToken = await refreshToken();
          failedRequestsQueue.forEach((req) => req.resolve(newAccessToken));
          failedRequestsQueue = [];

          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return API(originalRequest);
        } catch (refreshError) {
          failedRequestsQueue.forEach((req) => req.reject(refreshError));
          failedRequestsQueue = [];
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        });
      }
    }

    return Promise.reject(error.response?.data || error);
  }
);

export default API;
